import { FC, useState, useRef, useEffect } from 'react';

import { useNovaPoshtaSearch } from '../../hooks/useNovaPoshtaSearch';
import { Routes } from '../../models/routes';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';

interface Settlement {
    ref: string;
    name: string;
    region: string;
    district: string;
}

interface Props {
    value: string;
    onSelect: (settlement: {
        ref: string;
        name: string;
        region: string;
    }) => void;
    onFallbackInput: (value: string) => void;
    error?: string;
    initialFallback?: boolean;
}

export const UkrposhtaSettlementSelect: FC<Props> = ({
    value,
    onSelect,
    onFallbackInput,
    error,
    initialFallback = false,
}) => {
    const [query, setQuery] = useState(value);
    const [isOpen, setIsOpen] = useState(false);
    const [manualFallback] = useState(initialFallback);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const { results, isFallback, search, clear } =
        useNovaPoshtaSearch<Settlement>({
            url: Routes.ApiUkrposhtaSettlements,
            enabled: !manualFallback,
        });

    const useFallback = manualFallback || isFallback;

    useEffect(() => {
        setQuery(value);
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (val: string) => {
        setQuery(val);
        if (useFallback) {
            onFallbackInput(val);
        } else {
            search(val);
            setIsOpen(true);
        }
    };

    const handleSelect = (settlement: Settlement) => {
        setQuery(settlement.name);
        setIsOpen(false);
        clear();
        onSelect({
            ref: settlement.ref,
            name: settlement.name,
            region: settlement.region,
        });
    };

    return (
        <div ref={wrapperRef} className="relative space-y-1">
            <Label htmlFor="up_settlement">Населений пункт *</Label>
            <Input
                id="up_settlement"
                value={query}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder="Почніть вводити назву..."
                className={error ? 'border-red-500' : ''}
                autoComplete="off"
            />
            {error && <p className="text-xs text-red-500">{error}</p>}

            {isOpen && results.length > 0 && (
                <ul className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border bg-white shadow-lg dark:border-white/10 dark:bg-slate-900 dark:shadow-2xl">
                    {results.map((s) => (
                        <li
                            key={s.ref}
                            className="cursor-pointer px-3 py-2 text-sm hover:bg-muted/50 dark:text-slate-100 dark:hover:bg-slate-800"
                            onClick={() => handleSelect(s)}
                        >
                            <span className="font-medium">{s.name}</span>
                            {s.district && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                    {s.district} р-н
                                </span>
                            )}
                            {s.region && (
                                <span className="ml-1 text-xs text-muted-foreground">
                                    ({s.region})
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
